import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { 
  Search,
  Bookmark, 
  History,
  Users,
  Settings,
  Sparkles,
  Menu,
  X,
  Crown,
  Sliders,
  Shield,
  AlertTriangle,
  Flame,
  Radar,
} from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { LogOut } from 'lucide-react'
import { UsageCounter } from './UsageCounter'
import { useToast } from '@/contexts/ToastContext'

interface LayoutProps {
  children: React.ReactNode
}

const navigationItems = [
  {
    name: 'Búsqueda',
    href: '/',
    icon: Search,
    description: 'Buscar anuncios'
  },
  {
    name: 'Historial de Búsquedas',
    href: '/search-history',
    icon: History,
    description: 'Ver historial de búsquedas'
  },
  {
    name: 'Anunciantes en Seguimiento',
    href: '/tracked-advertisers',
    icon: Users,
    description: 'Monitorear anunciantes'
  },
  {
    name: 'Minero Autónomo',
    href: '/miner',
    icon: Radar,
    description: 'Busca solo sin parar'
  },
  {
    name: 'Capturas en Fuego',
    href: '/captured-ads',
    icon: Flame,
    description: 'Ganadores detectados solos'
  },
  {
    name: 'Anuncios Favoritos',
    href: '/saved-ads',
    icon: Bookmark,
    description: 'Anuncios guardados'
  },
]

export function Layout({ children }: LayoutProps) {
  const { showSuccess, showError } = useToast()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, logout, isImpersonating, originalAdmin, stopImpersonating } = useAuth()
  // Las cuentas viewer solo tienen una tab: la de capturas. El resto se oculta aquí y el
  // backend además les devuelve 403, así que ocultar el enlace no es la única barrera.
  const navVisible = user?.role === 'viewer'
    ? navigationItems.filter(i => i.href === '/captured-ads')
    : navigationItems

  const handleLogout = async () => {
    try {
      await logout()
      showSuccess('Sesión cerrada exitosamente')
    } catch (error) {
      showError('Error al cerrar sesión')
    }
  }

  // Get user initials
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  return (
    <div className="flex h-screen bg-stone-950">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          fixed inset-y-0 left-0 z-50 w-75 transform transition-transform duration-300 ease-in-out
          md:relative md:translate-x-0 md:transition-none
          sidebar
        `}
      >
        <div className="h-full p-6 flex flex-col bg-stone-900 border-r border-stone-800">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="text-left min-w-0">
              <h1 className="text-xl sm:text-2xl holographic-title font-bold tracking-tight">
                <Sparkles className="inline w-5 h-5 sm:w-6 sm:h-6 mr-2 text-secondary-400 align-middle" />
                Ads Finder PRO
              </h1>
            </div>
            
            {/* Mobile close button */}
            <button
              className="md:hidden btn-icon"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-2 py-6">
            {navVisible.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.href
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center p-3 rounded-xl transition-all duration-200
                    ${isActive 
                      ? 'bg-stone-100 text-stone-900 shadow-gum border border-stone-300' 
                      : 'text-stone-400 hover:bg-stone-800 hover:text-stone-100 border border-transparent hover:border-stone-700'
                    }
                  `}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  <div>
                    <div className="font-medium">{item.name}</div>
                    <div className={`text-xs ${isActive ? 'text-stone-600' : 'text-stone-500'}`}>{item.description}</div>
                  </div>
                </Link>
              )
            })}

            {/* Admin Navigation - Only show for admins */}
            {user?.role === 'admin' && (
              <>
                <div className="border-t border-stone-700 my-4"></div>
                <div className="text-xs text-stone-500 uppercase tracking-wider mb-2 px-3 font-medium">
                  Administración
                </div>
                <Link
                  to="/admin/users"
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center p-3 rounded-lg transition-all duration-300
                    ${location.pathname === '/admin/users'
                      ? 'bg-red-950/50 text-red-200 border border-red-800/80' 
                      : 'text-stone-400 hover:bg-red-950/30 hover:text-red-300 border border-transparent hover:border-red-900/50'
                    }
                  `}
                >
                  <Shield className="w-5 h-5 mr-3" />
                  <div>
                    <div className="font-medium">Gestión de Usuarios</div>
                    <div className="text-xs text-stone-500">Administrar usuarios y planes</div>
                  </div>
                </Link>
              </>
            )}
          </nav>

          {/* User Info Footer */}
          <div className="mt-auto pt-4 border-t border-stone-800">
            {user && (
              <div className="space-y-3">
                {/* Usage Counter */}
                <UsageCounter showDetails={false} className="mb-3" />

                {/* Compact User Info */}
                <div className="flex items-center justify-between px-2 py-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="w-8 h-8 bg-gradient-to-br from-secondary-700 to-stone-900 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-semibold">
                        {getInitials(user.name)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-100 truncate">
                        {user.name}
                      </p>
                      <p className="text-xs text-stone-400 truncate">
                        {user.role === 'admin' ? 'Admin' : 'Usuario'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Compact Action Buttons */}
                  <div className="flex items-center gap-1">
                    <Link
                      to="/user-plans"
                      className={`p-2 rounded-lg transition-colors ${
                        location.pathname === '/user-plans'
                          ? 'bg-secondary-900/40 text-secondary-300'
                          : 'text-stone-400 hover:text-stone-100 hover:bg-stone-800'
                      }`}
                      title="Planes y Límites"
                    >
                      <Crown className="w-4 h-4" />
                    </Link>
                    <Link
                      to="/settings"
                      className={`p-2 rounded-lg transition-colors ${
                        location.pathname === '/settings'
                          ? 'bg-secondary-900/40 text-secondary-300'
                          : 'text-stone-400 hover:text-stone-100 hover:bg-stone-800'
                      }`}
                      title="Configuración"
                    >
                      <Sliders className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                      title="Cerrar Sesión"
                    >
                      <LogOut className="w-4 h-4" />
                    </button>
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Impersonation Banner */}
        {isImpersonating && originalAdmin && (
          <div className="bg-yellow-500 text-yellow-900 px-4 py-3 flex items-center justify-between border-b border-yellow-600">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">
                Estás suplantando como: <strong>{user?.name}</strong> ({user?.email})
              </span>
            </div>
            <button
              onClick={stopImpersonating}
              className="px-4 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-yellow-900 font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Volver a {originalAdmin.name}
            </button>
          </div>
        )}

        {/* Header */}
        <header className="bg-stone-950/90 backdrop-blur-md border-b border-stone-800 p-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            {/* Mobile menu button */}
            <button
              className="md:hidden btn-icon"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            
           
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6 main-content">
          {children}
        </main>
      </div>

    </div>
  )
}
